import { isTabularConfigName, MAX_TABULAR_INPUT_FILES_PER_RUN } from '@atbash-labs/ontology-core'

import { SOURCE_LABELS, SOURCE_ORDER, defaultSub, specFor } from './constants'
import { pssTuningValid } from './pss'
import { tagMappingIsReady } from './tagMappingGrammars'
import type { PlanIssue, PrepValues, SourceId, SubOptionId, WizardState } from './types'

/**
 * Sources that can be the sole input to Add Site because they write (or
 * bootstrap onto) the ingest graph. Mapping / existing-resource / ServiceNow
 * paths assume a Site already exists.
 */
export const ADD_SITE_GRAPH_SOURCES: ReadonlySet<SourceId> = new Set([
  'scada',
  'plc',
  'robotics',
  'vision',
  'experion',
  'mtconnect',
  'dbdump',
  'sql',
  'soap',
  'pss',
  'srs',
  'safetymanager',
  'unstructured',
  'tabular'
])

/** Sources whose `existing_tags` mode binds an external historian to registered tags. */
export const EXTERNAL_HISTORY_SOURCE_IDS = ['pi', 'canary'] as const
export type ExternalHistorySourceId = (typeof EXTERNAL_HISTORY_SOURCE_IDS)[number]

export function isExternalHistorySource(id: SourceId): id is ExternalHistorySourceId {
  return (EXTERNAL_HISTORY_SOURCE_IDS as readonly SourceId[]).includes(id)
}

function bindsExistingTags(state: WizardState, id: ExternalHistorySourceId): boolean {
  return state.prep[id]?.tagMode === 'existing_tags'
}

export function sourceEstablishesAddSite(id: SourceId, state: WizardState): boolean {
  if (isExternalHistorySource(id)) return !bindsExistingTags(state, id)
  return ADD_SITE_GRAPH_SOURCES.has(id)
}

export function orderedSelected(selected: Set<SourceId>): SourceId[] {
  return SOURCE_ORDER.filter((id) => selected.has(id))
}

/** Empty accept (e.g. unstructured) means any file is allowed. */
export function fileMatchesAccept(fileName: string, accept?: string): boolean {
  const trimmed = (accept ?? '').trim()
  if (!trimmed) return true
  const lower = fileName.toLowerCase()
  return trimmed.split(',').some((token) => {
    const ext = token.trim().toLowerCase()
    if (!ext) return false
    return lower.endsWith(ext.startsWith('.') ? ext : `.${ext}`)
  })
}

/** Every file goes to one sandbox and one parser pass, so an upload is one run of 1..MAX files. */
export function tabularFileCountOk(count: number): boolean {
  return count >= 1 && count <= MAX_TABULAR_INPUT_FILES_PER_RUN
}

/**
 * The add-rows `replaceConfirmed` field. A run replaces the rows of every
 * earlier file of the config with one of its names, so the step waits while
 * the names are looked up and until someone confirms the replacement for this
 * exact config and set of files. A failed lookup does not block; the server
 * checks again when the run begins, and a replacement already confirmed for
 * these files still counts.
 */
export function tabularReplaceState(input: {
  lookup: 'none' | 'checking' | 'failed' | 'done'
  matched: boolean
  signature: string
  confirmedFor: string | undefined
}): '' | 'checking' | 'required' | 'true' {
  if (input.lookup === 'none') return ''
  if (input.lookup === 'failed') return input.confirmedFor === input.signature ? 'true' : ''
  if (input.lookup === 'checking') return 'checking'
  if (!input.matched) return ''
  return input.confirmedFor === input.signature ? 'true' : 'required'
}

export function buildPlanIssues(state: WizardState): PlanIssue[] {
  const out: PlanIssue[] = []
  const sel = state.selected

  if (sel.size === 0) {
    out.push({
      id: 'empty',
      title: 'Nothing selected yet',
      detail: 'Pick at least one datasource so the wizard has something to plan.',
      fixLabel: 'Back to selections',
      canWaive: false,
      severity: 'error'
    })
    return out
  }

  if (
    state.mode === 'new' &&
    !orderedSelected(sel).some((id) => sourceEstablishesAddSite(id, state))
  ) {
    const labels = orderedSelected(sel)
      .map((id) => SOURCE_LABELS[id])
      .join(', ')
    out.push({
      id: 'add-site-needs-plant-source',
      title: 'Add Site needs a plant source',
      detail:
        `${labels || 'The selected sources'} cannot create a site on their own. ` +
        'Mapping, tag import, PI or Canary historian-for-existing-tags, and ServiceNow attach to a site that already exists. ' +
        'Add a plant source (SCADA, PLC, robotics, MTConnect, standalone PI or Canary, a database, safety, documentation, SOAP, or PlantState Suite), or use Modify an existing site.',
      fixLabel: 'Back to selections',
      canWaive: false,
      severity: 'error'
    })
  }

  if (sel.has('plc') && !sel.has('ipmap')) {
    out.push({
      id: 'plc-ipmap',
      title: 'Recommended: add an IP / network map',
      detail:
        'A map links SCADA devices and OPC servers to the controllers that serve their tags. You can continue without it and add the map later.',
      fixLabel: 'Add IP / network map',
      canWaive: false,
      severity: 'warning',
      fixSourceId: 'ipmap'
    })
  }

  if (sel.has('robotics') && !sel.has('roboticsmap')) {
    out.push({
      id: 'robotics-map',
      title: 'Recommended: add a controller → PLC map',
      detail:
        'The map links robot I/O signals to the PLC tags that drive them. You can continue without it and add the map later.',
      fixLabel: 'Add robotics → PLC map',
      canWaive: false,
      severity: 'warning',
      fixSourceId: 'roboticsmap'
    })
  }

  if (sel.has('vision') && !sel.has('visionmap')) {
    out.push({
      id: 'vision-map',
      title: 'Recommended: add a vision system → PLC map',
      detail:
        'The map links each camera to its controller and the PLC tags it exchanges data through, using the controller L5X. You can continue without it and add the map later.',
      fixLabel: 'Add vision system → PLC map',
      canWaive: false,
      severity: 'warning',
      fixSourceId: 'visionmap'
    })
  }

  if (sel.has('srs') && sel.has('safetymanager') && !sel.has('safetymap')) {
    out.push({
      id: 'safety-manager-srs-map',
      title: 'Recommended: map Safety Manager to SRS',
      detail:
        'The two source ingests stay independent. Add the mapping job to link SRS functions and equipment to Safety Manager FLDs and points after both complete.',
      fixLabel: 'Add Safety Manager ↔ SRS mapping',
      canWaive: false,
      severity: 'warning',
      fixSourceId: 'safetymap'
    })
  }

  if (
    state.mode === 'new' &&
    sel.has('safetymap') &&
    (!sel.has('srs') || !sel.has('safetymanager'))
  ) {
    out.push({
      id: 'safety-map-needs-sources',
      title: 'Safety mapping needs both source ingests',
      detail:
        'For a new site, select both Safety Requirements Specification and Safety Manager dump before running their mapping job.',
      fixLabel: 'Back to selections',
      canWaive: false,
      severity: 'error'
    })
  }

  if ((sel.has('plc') || sel.has('robotics') || sel.has('mtconnect')) && !sel.has('scada')) {
    if (!state.waived.has('missing-scada')) {
      out.push({
        id: 'missing-scada',
        title: 'No SCADA / gateway backup selected',
        detail:
          'Controllers and telemetry typically bind through a gateway’s tag providers. Add a gateway backup if you have one.',
        fixLabel: 'Add SCADA backup',
        canWaive: true,
        waiveLabel: 'Continue without gateway',
        fixSourceId: 'scada',
        severity: 'error'
      })
    }
  }

  for (const id of ['erp', 'mes'] as SourceId[]) {
    if (!sel.has(id)) continue
    const sub = state.sub[id] ?? defaultSub(id)
    if (!sub && !state.waived.has(`${id}-sub`)) {
      out.push({
        id: `${id}-sub`,
        title: `${SOURCE_LABELS[id]} needs a connection choice`,
        detail:
          'Choose whether this datasource is reached through Ignition or a standalone database.',
        fixLabel: 'Back to selections',
        canWaive: false,
        severity: 'error'
      })
    }
  }

  return out
}

export function hasBlockingIssues(issues: PlanIssue[]): boolean {
  return issues.some((issue) => (issue.severity ?? 'error') === 'error')
}

export function scopeList(state: WizardState): Array<{ label: string; detail: string }> {
  return orderedSelected(state.selected).map((id) => {
    const sub = state.sub[id]
    const detail = sub ? `via ${sub}` : 'ready'
    return { label: SOURCE_LABELS[id], detail }
  })
}

export function isPrepReady(
  id: SourceId,
  prep: PrepValues | undefined,
  sub?: SubOptionId,
  flow: WizardState['flow'] = 'new'
): boolean {
  if (!prep) return false
  if (id === 'plcupdate') {
    // No-upload pull-sync: ready once at least one controller is selected.
    try {
      const items: unknown = JSON.parse(prep.fields.selectedItems ?? '[]')
      return Array.isArray(items) && items.length > 0
    } catch {
      return false
    }
  }
  if (id === 'pssupdate') {
    const { mirrorId, windowStart, windowEnd, pssAction } = prep.fields
    if (!mirrorId?.trim()) return false
    // A PssTag re-sync only needs the mirror; a journal backfill needs its window.
    if (pssAction?.trim() === 'sync_tags') return true
    if (!windowStart?.trim() || !windowEnd?.trim()) return false
    const start = Date.parse(windowStart)
    const end = Date.parse(windowEnd)
    return Number.isFinite(start) && Number.isFinite(end) && start < end
  }
  if (id === 'tagmap') return tagMappingIsReady(prep.fields)
  if (isExternalHistorySource(id) && prep.tagMode === 'existing_tags') {
    try {
      const sources: unknown = JSON.parse(prep.fields.historySources ?? '["ignition","opc"]')
      if (!Array.isArray(sources) || sources.length === 0) return false
    } catch {
      return false
    }
  }
  const selectedNames =
    prep.files.length > 0
      ? prep.files.map((file) => file.name)
      : prep.file
        ? [prep.file.name]
        : (prep.stagedFiles?.map((file) => file.name) ?? [])
  const primaryName = selectedNames[0]
  const spec = specFor(id, sub, prep.vendor, primaryName, prep.tagMode, flow, selectedNames.length)
  if (spec.drop) {
    const hasFile = !!prep.file || prep.files.length > 0 || (prep.stagedFiles?.length ?? 0) > 0
    if (!hasFile) return false
    // Tabular files are judged by TabularFileFields against the pinned
    // config's own accept list (or the build list) and reported as filesValid;
    // the generic drop-zone rule would wrongly refuse e.g. a .png a config takes.
    if (
      id !== 'tabular' &&
      selectedNames.some((name) => !fileMatchesAccept(name, spec.drop?.accept))
    ) {
      return false
    }
  }
  if (id === 'tabular') {
    const fields = prep.fields
    if (fields.action !== 'build' && fields.action !== 'add') return false
    if (fields.filesValid !== 'true') return false
    if (fields.action === 'build') {
      return isTabularConfigName((fields.configName ?? '').trim())
    }
    return (
      !!fields.configId &&
      Number(fields.configVersion) > 0 &&
      fields.upsertConfirmed !== 'required' &&
      fields.replaceConfirmed !== 'required' &&
      fields.replaceConfirmed !== 'checking'
    )
  }
  for (const f of spec.fields) {
    if (f.required && !(prep.fields[f.id] ?? '').trim()) return false
  }
  if (id === 'soap') {
    try {
      const operations = JSON.parse(prep.fields.enabledOperations ?? '[]')
      if (!Array.isArray(operations) || operations.length === 0) return false
      if (!(prep.fields.soapPreviewContractHash ?? '').trim()) return false
    } catch {
      return false
    }
  }
  if (id === 'pss') {
    if (!pssTuningValid(prep.fields)) return false
  }
  if (
    isExternalHistorySource(id) &&
    prep.tagMode === 'existing_tags' &&
    prep.fields.mappingValid !== 'true'
  ) {
    return false
  }
  return true
}

/**
 * External-history mapping (PI, Canary `existing_tags`) must run after the
 * tag-producing sources selected in the same wizard run, so its approval is
 * deferred to the run step.
 */
export function defersExternalHistoryMapping(state: WizardState, id: SourceId): boolean {
  return (
    isExternalHistorySource(id) &&
    state.selected.has(id) &&
    bindsExistingTags(state, id) &&
    (state.selected.has('scada') || state.selected.has('opctags'))
  )
}

export function defersPIHistoryMapping(state: WizardState): boolean {
  return defersExternalHistoryMapping(state, 'pi')
}

export function isSourcePrepReady(state: WizardState, id: SourceId): boolean {
  const prep = state.prep[id]
  const sub = state.sub[id] ?? defaultSub(id)
  if (!defersExternalHistoryMapping(state, id) || !prep) {
    return isPrepReady(id, prep, sub, state.flow)
  }
  return isPrepReady(
    id,
    {
      ...prep,
      fields: { ...prep.fields, mappingValid: 'true' }
    },
    sub,
    state.flow
  )
}

export function isOptionalPrepSkipped(state: WizardState, id: SourceId): boolean {
  if (!state.optionalSources.has(id)) return false
  const prep = state.prep[id]
  return !prep?.file && (prep?.files.length ?? 0) === 0 && (prep?.stagedFiles?.length ?? 0) === 0
}

export function allPrepReady(state: WizardState): boolean {
  return orderedSelected(state.selected).every(
    (id) => isOptionalPrepSkipped(state, id) || isSourcePrepReady(state, id)
  )
}

export function prepSummaryLine(state: WizardState): string {
  const ids = orderedSelected(state.selected)
  const ready = ids.filter((id) =>
    isPrepReady(id, state.prep[id], state.sub[id] ?? defaultSub(id), state.flow)
  )
  if (ids.length === 0) return 'Nothing to prepare'
  if (ready.length === ids.length) return `All ${ids.length} sources ready`
  return `${ready.length} of ${ids.length} sources ready`
}
